import React from 'react';
import { GlobeIcon, InstagramIcon, LinkedinIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { Logo } from '../Logo';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { useLanguage } from '../../contexts/LanguageContext';
import { email, phones, website } from '../../data/contact';
const TEAM_PHOTO_URL = 'https://cdn.magicpatterns.com/uploads/9w4R1e9Qm7iDRLUBkjiyMC/WhatsApp_Image_2026-08-19_at_9.39.32_AM.jpg';
const socials = [{
  label: 'LinkedIn',
  Icon: LinkedinIcon,
  href: 'https://www.linkedin.com/company/capital-winds-llc/'
}, {
  label: 'Instagram',
  Icon: InstagramIcon,
  href: 'https://www.instagram.com/standardconsultingadvice?igsi=MTQ0djNva3l5Z2w3NA=='
}];
const headingClass = 'text-[12px] font-semibold uppercase tracking-[0.18em] text-white/40';
export function Footer() {
  const {
    t
  } = useLanguage();
  return <footer className="border-t border-white/10 bg-ink pb-10 pt-20" data-id="element-125" data-mp="yr3ickyglq0d">
      <div className="mx-auto max-w-shell px-5 sm:px-8" data-id="element-126" data-mp="yoqyu3d1z7l7">
        {/* Brand + photo */}
        <div className="border-b border-white/10 pb-14" data-id="element-127" data-mp="1efzlcubq08dn">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between" data-id="element-128" data-mp="1iim84gtjb33z">
            <div data-id="element-129" data-mp="1na4b1af4bdrl">
              <Logo variant="light" data-id="element-130" data-mp="1nbp5z15cgypz" />
              <p className="mt-6 max-w-[460px] text-[15px] leading-relaxed text-white/60" data-id="element-131" data-mp="1ncqgn4ig7j76">
                {t.footer.tagline}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4" data-id="element-132" data-mp="vkj70o1d172a">
              <ul className="flex items-center gap-3" data-id="element-133" data-mp="zk9xeqmz5l6a">
                {socials.map(({
                label,
                Icon,
                href
              }) => <li key={label} data-id="element-134" data-mp="14iuwunuek0to">
                    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/12 text-white/70 transition-all duration-300 hover:-translate-y-0.5 hover:border-brand hover:bg-brand hover:text-white" data-id="element-135" data-mp="14il8t5sx43qm">
                      <Icon className="h-[18px] w-[18px]" aria-hidden="true" data-id="element-136" data-mp="1qb60nuqxvl81" />
                    </a>
                  </li>)}
              </ul>
              <span className="h-8 w-px bg-white/10" aria-hidden="true" data-id="element-137" data-mp="257exyqr9roht" />
              <LanguageSwitcher variant="dark" data-id="element-138" data-mp="20aesoi28uprr" />
            </div>
          </div>

          <figure className="mx-auto mt-12 w-full max-w-[960px] overflow-hidden rounded-4xl border border-white/10 shadow-lift" data-id="element-139" data-mp="1cipvooeh9d4h">
            <img src={TEAM_PHOTO_URL} alt={t.footer.teamPhotoAlt} className="block h-auto w-full transition-transform duration-700 hover:scale-[1.02]" data-id="element-140" data-mp="17lzh65ibmpl6" />
          </figure>
        </div>

        {/* Info columns */}
        <div className="grid gap-12 pt-14 sm:grid-cols-2 lg:grid-cols-3" data-id="element-141" data-mp="ts85orl14vmt">
          <div data-id="element-142" data-mp="otyc2leye33b">
            <h3 className={headingClass} data-id="element-143" data-mp="ougey0w4ffk7">{t.footer.floridaOffice}</h3>
            <p className="mt-5 flex gap-2.5 text-[14px] leading-relaxed text-white/70" data-id="element-144" data-mp="os3vfjapsx51">
              <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-145" data-mp="30f77udcl2voe" />
              <span data-id="element-146" data-mp="35a38g1vvyrd4">
                {t.footer.floridaAddress[0]}
                <br data-id="element-147" data-mp="2qkf750npfspe" />
                {t.footer.floridaAddress[1]}
              </span>
            </p>
            <h3 className={`mt-8 ${headingClass}`} data-id="element-148" data-mp="2viywlb1ibmmi">{t.footer.newYorkOffice}</h3>
            <p className="mt-5 flex gap-2.5 text-[14px] leading-relaxed text-white/70" data-id="element-149" data-mp="3j7i4hpgn9xe2">
              <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-150" data-mp="3o7e65i1vdcqg" />
              <span data-id="element-151" data-mp="3a7mmp1sl3452">
                {t.footer.newYorkAddress[0]}
                <br data-id="element-152" data-mp="3a966v3hd1zc4" />
                {t.footer.newYorkAddress[1]}
              </span>
            </p>
          </div>

          <div data-id="element-153" data-mp="1yry0icn5m95b">
            <h3 className={headingClass} data-id="element-154" data-mp="1yrerjlfhev3f">{t.footer.contact}</h3>
            <ul className="mt-5 space-y-4" data-id="element-155" data-mp="1yuar1e6z9rtz">
              {phones.map(phone => <li key={phone.href} data-id="element-156" data-mp="22uuokivgqxgn">
                  <a href={phone.href} className="flex items-center gap-2.5 text-[14px] text-white/70 transition-colors hover:text-white" data-id="element-157" data-mp="2j0ede0nrmyvp">
                    <PhoneIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-158" data-mp="29al9zmxs1q2j" />
                    {phone.display}
                  </a>
                </li>)}
              <li data-id="element-159" data-mp="31zigkssmlyfg">
                <a href={`mailto:${email}`} className="flex items-start gap-2.5 break-all text-[14px] text-white/70 transition-colors hover:text-white" data-id="element-160" data-mp="3201zabq3oquj">
                  <MailIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-161" data-mp="2s0kxi5dyyx3u" />
                  {email}
                </a>
              </li>
              <li data-id="element-162" data-mp="2o23n9cz956i7">
                <a href={website.href} className="flex items-center gap-2.5 text-[14px] text-white/70 transition-colors hover:text-white" data-id="element-163" data-mp="1glxmdkpk2olt">
                  <GlobeIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-164" data-mp="3o7xr4fjj5xf1" />
                  {website.display}
                </a>
              </li>
            </ul>
          </div>

          <div data-id="element-165" data-mp="1381s1fbj4wb">
            <h3 className={headingClass} data-id="element-166" data-mp="12pxchii5kdb">{t.footer.explore}</h3>
            <ul className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-1" data-id="element-167" data-mp="1152srw6u1fg">
              {t.nav.links.map(link => <li key={link.href} data-id="element-168" data-mp="atb2yowlfp5q">
                  <a href={link.href} className="text-[14px] text-white/70 transition-colors hover:text-white" data-id="element-169" data-mp="as9i5sprzoat">
                    {link.label}
                  </a>
                </li>)}
              <li data-id="element-170" data-mp="osk88tj9gyux">
                <a href="#consultation" className="text-[14px] font-semibold text-brand transition-colors hover:text-white" data-id="element-171" data-mp="ot2aq7wio9ba">
                  {t.footer.freeConsultation}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-3 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-172" data-mp="3tw4d5gc094gp">
          <p className="text-[13px] text-white/45" data-id="element-173" data-mp="3oxugt2ejnxti">
            © {new Date().getFullYear()} {t.footer.rights}
          </p>
          <p className="text-[13px] text-white/45" data-id="element-174" data-mp="1ra8vgmj9p9v">{t.footer.offices}</p>
        </div>
      </div>
    </footer>;
}